import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import TempCodeDialog from "@/components/TempCodeDialog";
import { formatApiErrorDetail } from "@/lib/api";
import { homePathForRole } from "@/lib/roleRouter";
import { AUTH } from "@/constants/testIds";

const LOCKER_BG =
  "https://static.prod-images.emergentagent.com/jobs/e16202cb-95d0-411c-902c-bd0d02e3145b/images/f5bc4a7fe6e1676168c6e0591718eb78ed19e3f94cf3d783330b28fced0a79a2.png";

// Subtle drop-shadow so cream text stays legible against the warm pro-shop
// photo without resorting to an overall green wash. Stronger than a normal
// shadow so it survives bright highlights on the equipment.
const TEXT_SHADOW = "0 1px 2px rgba(0,0,0,0.55), 0 0 14px rgba(0,0,0,0.35)";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, setUser } = useAuth();
  const [tempCodeOpen, setTempCodeOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(homePathForRole(user.role), { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      const from = location.state?.from;
      const dest = homePathForRole(u.role) !== "/dashboard" ? homePathForRole(u.role) : (from || "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen text-[#F9F8F4]">
      {/* Hero container — image is absolutely positioned inside this section,
          not on the whole page, so the Solutions section below can render
          on a fresh cream slab. */}
      <div className="relative overflow-hidden">
      {/* Full-bleed locker room photo */}
      <img
        src={LOCKER_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Gentle bottom-and-edges vignette — no green tint, just enough so the
          form inputs read against the warm wood when the user scrolls. */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.55)_100%)] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto min-h-screen px-6 sm:px-10 lg:px-16 py-10 sm:py-14 flex flex-col">
        {/* "EST. 1928" hallmark — small italic counterweight top-right */}
        <div
          className="hidden lg:block absolute top-14 right-16 text-right fade-up"
          style={{ animationDelay: "0.05s" }}
        >
          <div
            className="text-[11px] uppercase tracking-[0.32em] font-semibold"
            style={{ color: "#D9C68A", textShadow: TEXT_SHADOW }}
          >
            Established
          </div>
          <div
            className="font-serif italic text-3xl text-[#F9F8F4] mt-1"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Anno 2026
          </div>
        </div>

        {/* MEMBERS ONLY · brand wordmark */}
        <header className="max-w-2xl fade-up" style={{ animationDelay: "0.1s" }}>
          <img
            src="/logo.png"
            alt="My Crest Club"
            data-testid="login-logo"
            className="h-28 sm:h-36 w-auto mb-6 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
          <div
            className="text-[13px] uppercase tracking-[0.36em] font-semibold text-[#F9F8F4]"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Members Only
          </div>
          <h1
            className="font-serif text-6xl sm:text-7xl lg:text-8xl text-[#F9F8F4] mt-6 leading-[0.95]"
            style={{ textShadow: TEXT_SHADOW }}
          >
            My Crest Club
          </h1>
          <div
            className="font-serif text-3xl sm:text-4xl text-[#F9F8F4]/95 mt-4"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Special Orders
          </div>
        </header>

        {/* Sign-in panel — pinned top-right on desktop, full-width below on mobile */}
        <div
          className="mt-14 lg:mt-0 lg:absolute lg:top-44 lg:right-16 lg:w-[460px] fade-up"
          style={{ animationDelay: "0.25s" }}
        >
          <h2
            className="font-serif text-6xl lg:text-7xl text-[#F9F8F4] leading-none"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Sign in
          </h2>
          <p
            className="mt-4 text-lg text-[#F9F8F4]/90"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Welcome back to the members&rsquo; portal.
          </p>

          <div className="mt-10">
            <div
              className="text-[13px] uppercase tracking-[0.24em] font-semibold"
              style={{ color: "#D9C68A", textShadow: TEXT_SHADOW }}
            >
              Which entrance?
            </div>
            <p
              className="font-serif italic text-lg text-[#F9F8F4]/95 mt-2"
              style={{ textShadow: TEXT_SHADOW }}
            >
              Members sign in below · Pro-shop staff use The Back Office
            </p>
          </div>

          {error && (
            <div
              data-testid={AUTH.errorBanner}
              className="mt-6 px-3 py-2 text-sm border border-[#F5C7C7] text-[#FFE3E3]"
              style={{ background: "rgba(138,26,26,0.55)" }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-10 space-y-7">
            <Field
              id="email"
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              testId={AUTH.loginEmail}
            />
            <Field
              id="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              testId={AUTH.loginPassword}
            />

            <div className="flex items-center justify-between pt-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid={AUTH.loginSubmit}
                className="font-serif text-3xl tracking-[0.24em] uppercase text-[#F9F8F4] hover:text-[#D9C68A] transition-colors disabled:opacity-60"
                style={{ textShadow: TEXT_SHADOW }}
              >
                {submitting ? "Signing in…" : "Sign In"}
              </button>
              <Link
                to="/forgot-password"
                className="text-[12px] uppercase tracking-[0.24em] text-[#F9F8F4]/80 hover:text-[#D9C68A]"
                style={{ textShadow: TEXT_SHADOW }}
              >
                Forgot password?
              </Link>
              <button
                type="button"
                onClick={() => setTempCodeOpen(true)}
                data-testid="login-temp-code-link"
                className="text-[12px] uppercase tracking-[0.24em] text-[#F9F8F4]/80 hover:text-[#D9C68A] underline-offset-4 hover:underline"
                style={{ textShadow: TEXT_SHADOW }}
              >
                Send Temp Code
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-10 flex items-center gap-4">
            <span className="flex-1 h-px bg-[#F9F8F4]/40" />
            <span
              className="text-[12px] uppercase tracking-[0.36em] text-[#F9F8F4]/80"
              style={{ textShadow: TEXT_SHADOW }}
            >
              Or
            </span>
            <span className="flex-1 h-px bg-[#F9F8F4]/40" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            data-testid={AUTH.loginGoogle}
            className="mt-7 w-full flex items-center justify-center gap-3 py-3 text-[13px] uppercase tracking-[0.24em] font-semibold text-[#F9F8F4] hover:text-[#D9C68A] transition-colors"
            style={{ textShadow: TEXT_SHADOW }}
          >
            <GoogleGlyph />
            Continue with Google
          </button>

          <p
            className="mt-12 text-base text-[#F9F8F4]/95"
            style={{ textShadow: TEXT_SHADOW }}
          >
            Been invited by your club?{" "}
            <Link
              to="/register"
              data-testid={AUTH.loginRegisterLink}
              className="text-[#D9C68A] underline underline-offset-4 font-semibold hover:text-[#F9F8F4]"
            >
              Activate your account
            </Link>
          </p>
        </div>

        {/* Spacer that pushes the admin card to the bottom on desktop */}
        <div className="hidden lg:block flex-1" />

        {/* Admin / Back Office card — floats bottom-left, mockup-faithful green panel */}
        <aside
          className="mt-14 lg:mt-0 lg:self-start max-w-md fade-up"
          style={{ animationDelay: "0.4s" }}
        >
          <div className="relative overflow-hidden border border-[#D9C68A]/30 bg-[#1A422D]/95 text-[#F9F8F4] p-8 shadow-2xl">
            {/* Decorative circle (mockup detail) */}
            <div className="absolute -right-12 -top-12 w-44 h-44 rounded-full bg-[#0F2A1C]/60" />
            <div className="relative">
              <div
                className="text-[13px] uppercase tracking-[0.24em] font-semibold"
                style={{ color: "#D9C68A" }}
              >
                Administrator Access
              </div>
              <h3 className="font-serif text-3xl mt-2 leading-snug">
                The Pro Shop Back Office
              </h3>
              <p className="text-base text-[#F9F8F4]/85 mt-3 leading-relaxed">
                Staff portal for placing vendor orders, messaging members, and
                tracking fulfillment.
              </p>
              <Link
                to="/admin/login"
                data-testid="login-admin-cta"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 bg-[#D9C68A] text-[#1A422D] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#E5D6A8] transition-colors"
              >
                Club Login <span aria-hidden>→</span>
              </Link>
              {/* Platform-owner (super admin) entry — discreet single-line
                  link beneath the staff CTA so it doesn't compete visually
                  with the staff back-office login but is reachable without
                  having to remember the /super-admin/login URL. */}
              <div className="mt-6 pt-5 border-t border-[#D9C68A]/20 space-y-3">
                <Link
                  to="/super-admin/login"
                  data-testid="login-super-admin-link"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#D9C68A]/85 hover:text-[#D9C68A] underline-offset-4 hover:underline"
                >
                  Platform Administrator Login <span aria-hidden>→</span>
                </Link>
                <br />
                <Link
                  to="/vendors/back-office"
                  data-testid="login-vendor-back-office-link"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[#D9C68A]/85 hover:text-[#D9C68A] underline-offset-4 hover:underline"
                >
                  Vendor Team Passkey Sign-In <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </div>
        </aside>
      </div>
      </div>
      {/* --- end hero container --- */}

      {/* --- Prompt 2: Solutions section (multi-audience) --- */}
      <SolutionsSection />

      {/* Footer — Build Guide Phase 2: For Clubs / For Vendors / Support links.
          Members are invitation-only so no `For Members` link on the marketing
          site. Privacy/Terms/Support round out the compliance footer. */}
      <footer className="relative z-10 bg-[#1A422D] text-[#F9F8F4]/85 pt-6 pb-6 px-8 lg:px-14 flex flex-wrap gap-5 text-[11px] uppercase tracking-[0.22em]">
        <Link to="/clubs" data-testid="login-footer-for-clubs" className="hover:text-[#D9C68A] transition-colors">For Clubs</Link>
        <Link to="/vendors" data-testid="login-footer-for-vendors" className="hover:text-[#D9C68A] transition-colors">For Vendors</Link>
        <Link to="/pricing" data-testid="login-footer-pricing" className="hover:text-[#D9C68A] transition-colors">Pricing</Link>
        <Link to="/about" data-testid="login-footer-about" className="hover:text-[#D9C68A] transition-colors">About</Link>
        <Link to="/privacy" data-testid="login-footer-privacy" className="hover:text-[#D9C68A] transition-colors">Privacy</Link>
        <Link to="/terms" data-testid="login-footer-terms" className="hover:text-[#D9C68A] transition-colors">Terms</Link>
        <Link to="/contact" data-testid="login-footer-support" className="hover:text-[#D9C68A] transition-colors">Support</Link>
        <span className="text-[#F9F8F4]/55 ml-auto">&copy; My Crest Club · Anno 2026</span>
      </footer>

      <TempCodeDialog
        open={tempCodeOpen}
        onClose={() => setTempCodeOpen(false)}
        roleLabel="Member Portal or Golf Shop"
        onSuccess={(u, isSA) => {
          setUser(u);
          setTempCodeOpen(false);
          if (isSA) { navigate("/super-admin", { replace: true }); return; }
          const dest = homePathForRole(u.role);
          navigate(dest, { replace: true });
        }}
      />
    </div>
  );
}

/**
 * Solutions section (Prompt 2) — 3 audience cards rendered on a cream slab
 * BELOW the hero photo, so the luxury locker-room hero remains the visual
 * anchor while the multi-audience narrative gets its own real estate.
 */
function SolutionsSection() {
  const CARDS = [
    {
      title: "Private Clubs",
      copy: [
        "Manage member requests.",
        "Communicate with vendors.",
        "Increase merchandise sales.",
        "Improve member satisfaction.",
      ],
      cta: { label: "Learn More", to: "/clubs", testId: "solutions-clubs-cta" },
      testId: "solutions-card-clubs",
      featured: true,
    },
    {
      title: "Vendors",
      copy: [
        "Receive qualified product requests.",
        "Upload catalogs.",
        "Manage quotes.",
        "Grow sales with private clubs.",
      ],
      cta: { label: "Become a Vendor", to: "/vendors", testId: "solutions-vendors-cta" },
      testId: "solutions-card-vendors",
    },
  ];
  return (
    <section
      data-testid="login-solutions-section"
      className="relative z-10 mt-16 bg-[#F9F8F4] text-[#1A1A1A] py-20 sm:py-24"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
            The Private Club Marketplace
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] mt-4 leading-[1.05]">
            The Private Club Marketplace for Members, Golf Shops, and Premium Vendors.
          </h2>
          <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">
            Connecting members, golf professionals, and premium brands through one intelligent ordering platform.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="solutions-hero-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors"
            >
              Schedule Demo <span aria-hidden>→</span>
            </Link>
            <a
              href="#sign-in"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              data-testid="solutions-hero-signin"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
            >
              Sign In
            </a>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {CARDS.map((card) => (
            <div
              key={card.title}
              data-testid={card.testId}
              className={`p-8 border transition-colors ${
                card.featured
                  ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
                  : "bg-white text-[#1A1A1A] border-[#E5E3D8] hover:border-[#B59A5A]"
              }`}
            >
              <div className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${card.featured ? "text-[#D9C68A]" : "text-[#B59A5A]"}`}>
                For
              </div>
              <h3 className={`mt-2 font-serif text-3xl ${card.featured ? "text-[#F9F8F4]" : "text-[#1A422D]"}`}>
                {card.title}
              </h3>
              <ul className={`mt-6 space-y-3 text-sm ${card.featured ? "text-[#F9F8F4]/90" : "text-[#4A4A4A]"}`}>
                {card.copy.map((line) => (
                  <li key={line} className="flex gap-2">
                    <span className={card.featured ? "text-[#D9C68A]" : "text-[#B59A5A]"} aria-hidden>·</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to={card.cta.to}
                  data-testid={card.cta.testId}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                    card.featured
                      ? "bg-[#D9C68A] text-[#1A422D] hover:bg-[#E5D6A8]"
                      : "border border-[#1A422D] text-[#1A422D] hover:bg-[#1A422D] hover:text-[#F9F8F4]"
                  }`}
                >
                  {card.cta.label} <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Already Invited? — Members-only entry point per Build Guide Phase 2.
            Members aren't a marketing target, but existing members should still
            find their way in from the landing page. Sits BELOW the club/vendor
            cards so the primary narrative stays focused on club + vendor value. */}
        <div
          data-testid="already-invited-section"
          className="mt-16 max-w-3xl mx-auto text-center py-10 px-8 border border-[#E5E3D8] bg-white/70"
        >
          <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
            Already Invited?
          </div>
          <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-[#1A422D]">
            Members sign in above.
          </h3>
          <p className="mt-3 text-sm text-[#4A4A4A]">
            My Crest Club is invitation-only for members. If your club has invited you,
            use the sign-in form at the top of this page.
          </p>
          <a
            href="#sign-in"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            data-testid="already-invited-signin-cta"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 border border-[#1A422D] text-[#1A422D] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Sign In <span aria-hidden>↑</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, type, value, onChange, required, placeholder, autoComplete, testId }) {
  const [showPw, setShowPw] = React.useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword && showPw ? "text" : type;
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[13px] uppercase tracking-[0.24em] font-semibold text-[#F9F8F4]"
        style={{ textShadow: TEXT_SHADOW }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={effectiveType}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          data-testid={testId}
          className={`mt-3 w-full bg-transparent border-0 border-b border-[#F9F8F4]/55 text-[#F9F8F4] placeholder-[#F9F8F4]/40 py-2.5 px-0 focus:outline-none focus:border-[#D9C68A] transition-colors text-xl ${isPassword ? "pr-9" : ""}`}
          style={{ textShadow: TEXT_SHADOW }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPw((v) => !v)}
            tabIndex={-1}
            aria-label={showPw ? "Hide password" : "Show password"}
            data-testid={testId ? `${testId}-eye` : undefined}
            className="absolute right-0 top-1/2 mt-1.5 -translate-y-1/2 p-1.5 text-[#F9F8F4]/70 hover:text-[#D9C68A] focus:text-[#D9C68A] focus:outline-none"
          >
            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.836.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.331A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.963H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.037l3.007-2.331z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.441 1.346l2.582-2.581C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.963L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
